import { RBAClient } from 'rba-client'

const client = RBAClient.getInstance()

const state = {
  listing: [],
  totalCount: 0,
  selectedTransaction: null,
  loading: false,
  error: null,

  // Default query parameters matching GET /api/v1/transactions
  filters: {
    page: 1,
    limit: 20,
    sector: null, // 'PAYMENT_GATEWAY' | 'SECURITIES'
    transactionId: '',
    partyId: '',
    partyType: '',
    accountId: '',
    accountOpeningTimestamp: null,
    direction: 'ANY', // 'INBOUND' | 'OUTBOUND' | 'ANY'
    channel: null, // 'WIRE' | 'ACH' | 'CRYPTO' | ...
    status: null, // 'PENDING' | 'SETTLED' | 'FAILED' | 'REVERSED' | 'COMPLETED'
    minAmountHKD: null,
    maxAmountHKD: null,
    fromTimestamp: null,
    toTimestamp: null,
    counterpartyId: '',
    counterpartyCountry: '',
    ticker: '',
    assetClass: null, // 'EQUITY_BLUE_CHIP' | 'EQUITY_PENNY' | 'OPTIONS' | ...
    sortBy: 'transactionTimestamp',
    sortOrder: 'desc'
  },

  // Enums extracted directly from Swagger OpenAPI DTOs
  enums: {
    sectors: ['PAYMENT_GATEWAY', 'SECURITIES'],
    directions: ['INBOUND', 'OUTBOUND', 'ANY'],
    channels: [
      'WIRE', 'ACH', 'CRYPTO', 'CASH_DEPOSIT', 'ATM_WITHDRAWAL',
      'CARD', 'CREDIT_CARD', 'DEBIT_CARD', 'APPLE_PAY', 'GOOGLE_PAY',
      'ALTERNATIVE_PAYMENT', 'INTERNAL_FX_CONVERSION', 'JOURNAL_TRANSFER'
    ],
    statuses: ['PENDING', 'SETTLED', 'FAILED', 'REVERSED', 'COMPLETED'],
    assetClasses: ['EQUITY_BLUE_CHIP', 'EQUITY_PENNY', 'OPTIONS', 'MUTUAL_FUND', 'CRYPTO']
  }
}

const getters = {
  transactions: (state) => state.listing,
  totalCount: (state) => state.totalCount,
  selectedTransaction: (state) => state.selectedTransaction,
  isLoading: (state) => state.loading,
  error: (state) => state.error,

  // Sanitizes filters to remove empty strings, nulls, or undefined values before sending API requests
  activeFilters: (state) => {
    return Object.entries(state.filters).reduce((acc, [key, val]) => {
      if (val !== null && val !== '' && val !== undefined) {
        acc[key] = val
      }
      return acc
    }, {})
  },

  // UI Dropdown option helpers derived from API enums
  channelOptions: (state) => state.enums.channels.map(ch => ({ label: ch.replace(/_/g, ' '), value: ch })),
  statusOptions: (state) => state.enums.statuses.map(st => ({ label: st, value: st })),
  directionOptions: (state) => state.enums.directions.map(dir => ({ label: dir, value: dir })),
  sectorOptions: (state) => state.enums.sectors.map(sec => ({ label: sec.replace(/_/g, ' '), value: sec }))
}

const mutations = {
  SET_LOADING (state, isLoading) {
    state.loading = isLoading
  },
  SET_ERROR (state, error) {
    state.error = error
  },
  SET_TRANSACTIONS (state, { items, total }) {
    state.listing = items
    state.totalCount = total
  },
  SET_SELECTED_TRANSACTION (state, transaction) {
    state.selectedTransaction = transaction
  },
  UPDATE_FILTERS (state, payload) {
    state.filters = { ...state.filters, ...payload }
  },
  RESET_FILTERS (state) {
    state.filters = {
      page: 1,
      limit: 20,
      sector: null,
      transactionId: '',
      partyId: '',
      partyType: '',
      accountId: '',
      accountOpeningTimestamp: null,
      direction: 'ANY',
      channel: null,
      status: null,
      minAmountHKD: null,
      maxAmountHKD: null,
      fromTimestamp: null,
      toTimestamp: null,
      counterpartyId: '',
      counterpartyCountry: '',
      ticker: '',
      assetClass: null,
      sortBy: 'transactionTimestamp',
      sortOrder: 'desc'
    }
  }
}

const actions = {
  // GET /api/v1/transactions
  // async fetchTransactions ({ commit, getters }) {
  //   commit('SET_LOADING', true)
  //   commit('SET_ERROR', null)
  //   try {
  //     const params = getters.activeFilters

  //     // Log available keys on the SDK client to verify exact method/property names
  //     console.log('RBAClient properties:', Object.keys(client))

  //     const response = client.tm_transaction ? await client.tm_transaction.findWhere({ where: params }) : await client.http.get('/api/v3/transaction_monitoring/transactions', { params })

  //     const data = response.data || response
  //     const items = Array.isArray(data) ? data : (data.items || data.data || [])
  //     const total = Array.isArray(data) ? data.length : (data.total || items.length)

  //     commit('SET_TRANSACTIONS', { items, total })
  //     return response
  //   } catch (err) {
  //     const message = err.response?.data?.message || err.message || 'Failed to fetch transactions'
  //     commit('SET_ERROR', message)
  //     throw err
  //   } finally {
  //     commit('SET_LOADING', false)
  //   }
  // },
  async fetchTransactions ({ commit, getters }, payload) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
    // 1. Normalize payload: supports passing an array of IDs directly OR a params object
      console.log('fetchTransactions.payload')
      console.log(payload)
      const customParams = Array.isArray(payload)
        ? { transactionIds: payload }
        : (payload || {})

      // 2. Merge active filters with passed transactionIds
      const params = {
        ...getters.activeFilters,
        ...customParams
      }

      // 3. Make HTTP or SDK call with merged params
      const response = client.tm_transaction
        ? await client.tm_transaction.findWhere({ where: params })
        : await client.http.get('/api/v3/transaction_monitoring/transactions', { params })

      const data = response.data || response
      const items = Array.isArray(data) ? data : (data.items || data.data || [])
      const total = Array.isArray(data) ? data.length : (data.total || items.length)
      console.log('items')
      console.log(items)
      commit('SET_TRANSACTIONS', { items, total })
      return response
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch transactions'
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // GET /api/v1/transactions/{id}
  async fetchTransactionById ({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      console.log('client.tm_transaction')
      console.log(client.tm_transaction)
      const response = client.tm_transaction ? await client.tm_transaction.findOne(id) : client.http.get(`/api/v3/transaction_monitoring/transactions${id}`)
      const data = response.data || response
      commit('SET_SELECTED_TRANSACTION', data)
      return data
    } catch (err) {
      const message = err.response?.data?.message || err.message || `Failed to fetch transaction ${id}`
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // POST /api/v1/screening/ingest (IngestTransactionDto)
  async ingestTransaction ({ dispatch, commit }, payload) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = client.tm_transaction ? await client.screening.ingest(payload) : client.http.post('/api/v3/transaction_monitoring/transactions', payload)
      // Refresh listing after transaction ingestion
      await dispatch('fetchTransactions')
      return response.data || response
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to ingest transaction'
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Filter updates triggered by table controls
  setFilters ({ commit, dispatch }, filterPayload) {
    commit('UPDATE_FILTERS', filterPayload)
    return dispatch('fetchTransactions')
  },

  resetFilters ({ commit, dispatch }) {
    commit('RESET_FILTERS')
    return dispatch('fetchTransactions')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
